import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import intl from 'react-intl-universal';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { createNewProject } from 'state/Projects/projectsSlice';
import { selectUser } from 'state/User/userSlice';
import { getOrganizationProcesses } from 'state/Processes/processesSlice';
import {
  getLDUsers,
  getFacilitatorUsers,
} from 'state/UsersManagement/usersManagementSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { updateRequestLinkedProjects } from 'state/ActiveRequest/activeRequestSlice';
import {
  fetchProjectTemplatesForSelection,
  selectAllProjectTemplates,
} from 'state/ProjectTemplates/projectTemplatesSlice';
import { PROJECT_PRIVACY, PATHS } from 'utils/constants';
import useModal from 'Hooks/useModal';
import ProjectCreationForm from 'Pages/ProjectsListPage/components/ProjectCreationForm';
import kanbanCardPlus from 'assets/icons/create-project.svg';
import { NewProject, Request } from '../../../utils/customTypes';
import { attachTaskBundleToProject } from 'state/TasksBundle/tasksBundleSlice';
import { closePanel } from 'state/SidePanel/sidePanelSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toUtcNoonIso } from 'utils/calendarDates';

const ProjectCreationModal = ({
  isOpen,
  setModalOpen,
  requestId,
  requestData,
  sidePanel = false,
  overlayClassName = 'z-10',
}: {
  isOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  requestId: string;
  requestData: Request;
  sidePanel?: boolean;
  overlayClassName?: string;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    newTaskTypes = false,
    teamScopingProjectTemplates = false,
    relativeDates = false,
  } = useFlags();
  const currentUser = useSelector(selectUser);
  const projectTemplates = useSelector(selectAllProjectTemplates);
  const initialBusinessTeamId = requestData.businessTeams?.[0]?.id;

  const buildInitialFields = useCallback(() => {
    return {
      title: requestData.title || '',
      description: requestData.description,
      category_id: '',
      privacy: '',
      process_id: '',
      project_template_id: '',
      // CP-10907: stamp today's LOCAL calendar day — a raw instant reads
      // back as tomorrow via UTC parts for west-of-UTC users, and its raw
      // day is what the end-date-first picker path re-emits onto the wire.
      startDate: toUtcNoonIso(new Date()),
      targetCompletionDate: '',
      priority: requestData.ldPriority,
      businessTeams: initialBusinessTeamId ? [initialBusinessTeamId] : [],
      owners: [
        ...new Set([
          ...(requestData.owners?.map((owner) => owner.id) || []),
          currentUser.id,
        ]),
      ],
      task_bundle_id: '',
    };
  }, [
    requestData.title,
    requestData.description,
    requestData.ldPriority,
    requestData.owners,
    initialBusinessTeamId,
    currentUser.id,
  ]);

  const [fieldsValues, setFieldsValues] = useState(buildInitialFields);
  const [learningTeamIds, setLearningTeamIds] = useState<string[]>([]);
  const { Modal, modalProps, openModal, closeModal } = useModal();

  useEffect(() => {
    if (isOpen) {
      setFieldsValues(buildInitialFields());
      setLearningTeamIds([]);
      setCanCreateProject(false);
    }
  }, [buildInitialFields, isOpen]);

  useEffect(() => {
    const init = async () => {
      return await Promise.all([
        dispatch(getOrganizationProcesses()),
        dispatch(getLDUsers()),
        dispatch(getFacilitatorUsers()),
        dispatch(
          fetchProjectTemplatesForSelection({
            useNewApi: teamScopingProjectTemplates,
          })
        ),
      ]);
    };
    if (isOpen && !modalProps.isOpen) {
      init();
      openModal(modalProps);
    } else if (!isOpen && modalProps.isOpen) {
      closeModal();
    }
  }, [
    dispatch,
    isOpen,
    openModal,
    closeModal,
    modalProps,
    teamScopingProjectTemplates,
  ]);

  const updateValue = (newValue: any) =>
    setFieldsValues((prevData) => ({ ...prevData, ...newValue }));

  const [canCreateProject, setCanCreateProject] = useState(false);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    closeModal();
  }, [closeModal, setModalOpen]);

  const createProject = useCallback(
    async (redirectToProject?: boolean) => {
      const projectData: NewProject = {
        ...fieldsValues,
        projectRequests: [requestId],
      };
      const { task_bundle_id, ...newProjectData } = projectData;
      if (projectTemplates.length === 1) {
        newProjectData.project_template_id = projectTemplates[0].id;
      }
      if (fieldsValues.privacy === PROJECT_PRIVACY.TEAM) {
        newProjectData.learningTeams = learningTeamIds;
      }
      const createdProject = await dispatch(
        createNewProject({ ...newProjectData })
      );
      const newProjectId = get(createdProject, 'payload.data.id', '');
      handleCloseModal();

      if (newProjectId && task_bundle_id) {
        dispatch(
          attachTaskBundleToProject({
            projectId: newProjectId,
            taskBundleId: task_bundle_id,
            useNewApi: newTaskTypes,
            applyDates: relativeDates,
          })
        );
      }

      if (newProjectId && redirectToProject) {
        history.push(`${PATHS.PROJECT_PAGE}/${newProjectId}`);
      } else {
        dispatch(
          updateRequestLinkedProjects(get(createdProject, 'payload.data'))
        );
      }
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
          notificationMessage: intl.get(
            'REQUEST_PAGE.TOP_BAR.CREATE_PROJECT_MODAL.SUCCESS_MESSAGE',
            {
              projectTitle: fieldsValues.title,
            }
          ),
        })
      );
    },
    [
      fieldsValues,
      requestId,
      projectTemplates,
      dispatch,
      handleCloseModal,
      learningTeamIds,
      newTaskTypes,
      relativeDates,
      history,
    ]
  );

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get(
          'REQUEST_PAGE.TOP_BAR.CREATE_PROJECT_MODAL.SAVE_OPEN'
        ),
        variant: 'primary',
        disabled: !canCreateProject,
        onClick: async () => {
          await createProject(true);
          sidePanel && dispatch(closePanel());
        },
        'data-cy': 'save-open-project',
      },
      {
        children: intl.get(
          'REQUEST_PAGE.TOP_BAR.CREATE_PROJECT_MODAL.SAVE_NOW'
        ),
        variant: 'secondary',
        disabled: !canCreateProject,
        onClick: () => createProject(),
        'data-cy': 'save-project-now',
      },
      {
        children: intl.get('REQUEST_PAGE.TOP_BAR.CREATE_PROJECT_MODAL.CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModal,
        'data-cy': 'cancel-project-creation',
      },
    ],
    [canCreateProject, createProject, handleCloseModal, sidePanel, dispatch]
  );

  const checkEmpty = (value: any) => {
    if (Array.isArray(value)) {
      return isEmpty(value);
    }
    return value == null || value === '';
  };

  useEffect(() => {
    const { task_bundle_id, description, priority, ...originalRequiredFields } =
      fieldsValues;
    let requiredFields: Partial<NewProject> = { ...originalRequiredFields };
    if (projectTemplates.length === 1) {
      const { project_template_id, ...otherFields } = requiredFields;
      requiredFields = { ...otherFields };
    }
    if (
      requiredFields.privacy === PROJECT_PRIVACY.TEAM &&
      isEmpty(learningTeamIds)
    ) {
      canCreateProject && setCanCreateProject(false);
    } else if (
      !Object.values(requiredFields).some((value) => checkEmpty(value))
    ) {
      !canCreateProject && setCanCreateProject(true);
    } else {
      canCreateProject && setCanCreateProject(false);
    }
  }, [canCreateProject, fieldsValues, learningTeamIds, projectTemplates]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      {...modalProps}
      title={intl.get('REQUEST_PAGE.TOP_BAR.CREATE_PROJECT_MODAL.TITLE')}
      titleIcon={{
        name: 'kanban-card-plus',
        src: kanbanCardPlus,
        'data-testid': 'request-create-project',
      }}
      aria-label={intl.get('REQUEST_PAGE.TOP_BAR.CREATE_PROJECT_MODAL.TITLE')}
      childrenClassName='max-h-full'
      size='large'
      closeModal={handleCloseModal}
      data-cy='create-project-modal'
      actionButtons={actionButtons}
      overlayClassName={overlayClassName}
    >
      <ProjectCreationForm
        description={intl.get('PROJECT_DETAIL.FILL_IN_INFORMATION_TITLE')}
        setLearningTeamIds={setLearningTeamIds}
        updateValue={updateValue}
        data={fieldsValues}
        requestToProjectCreation
      />
    </Modal>
  );
};

export default ProjectCreationModal;
